/* global Word */

import { useEffect, useRef, useCallback } from 'react';
import type { TemplateProfile } from '../../templates/types';
import { renumberExhibitTables } from '../../word/exhibit/renumbering';
import { logger } from '../../core/logging';

/** Matches "Exhibit N:" headers — same pattern as renumbering.ts */
const EXHIBIT_HEADER_REGEX = /\bExhibit\s+\d+\s*:/gi;

/**
 * Counts how many "Exhibit N:" labels exist in the document body.
 * Uses a single body.text load — cheap and consistent with renumbering.ts.
 */
async function getExhibitCount(context: Word.RequestContext): Promise<number> {
  context.document.body.load('text');
  await context.sync();
  const bodyText = context.document.body.text ?? '';
  // exec-loop avoids matchAll (requires ES2020; tsconfig targets ES2015).
  // Reset lastIndex before each scan because the regex has the 'g' flag.
  EXHIBIT_HEADER_REGEX.lastIndex = 0;
  let count = 0;
  while (EXHIBIT_HEADER_REGEX.exec(bodyText) !== null) {
    count++;
  }
  return count;
}

/**
 * Hook: automatically renumbers exhibits when the user deletes one.
 *
 * Polls the document every 2 s via Word.run — the same pattern used
 * throughout this codebase.  When the exhibit count drops (deletion
 * detected), renumberExhibitTables is called immediately.
 */
export function useAutoRenumber(profile: TemplateProfile): void {
  const exhibitCountRef = useRef<number>(0);
  const isRunningRef   = useRef<boolean>(false);

  // Store latest profile in a ref so the polling callback stays stable.
  const profileRef = useRef<TemplateProfile>(profile);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  const checkAndRenumber = useCallback(async (): Promise<void> => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    try {
      await Word.run(async (context) => {
        const currentCount = await getExhibitCount(context);
        if (currentCount < exhibitCountRef.current) {
          logger.info('Exhibit deletion detected; auto-renumbering', {
            before: exhibitCountRef.current,
            after:  currentCount,
          });
          await renumberExhibitTables(context, profileRef.current);
          // Re-read after renumber to keep the baseline accurate.
          exhibitCountRef.current = await getExhibitCount(context);
        } else {
          // Keep baseline in sync for insertions as well.
          exhibitCountRef.current = currentCount;
        }
      });
    } catch (err) {
      logger.error('Auto-renumber check failed', { error: err });
    } finally {
      isRunningRef.current = false;
    }
  }, []); // deps empty — function only reads refs

  useEffect(() => {
    // Seed the initial count once on mount.
    void Word.run(async (context) => {
      exhibitCountRef.current = await getExhibitCount(context);
    }).catch((err) => {
      logger.error('Auto-renumber: initial count failed', { error: err });
    });

    // Poll every 2 s.  Simple; no Office event API overload issues.
    const intervalId = setInterval(() => {
      void checkAndRenumber();
    }, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkAndRenumber]);
}
